import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { Form as FinalForm, Field } from 'react-final-form';
import { Modal, Button, FieldTextInput, IconSpinner } from '../../components';
import {
  required,
  composeValidators,
  emailFormatValid,
  emailDomainNotExampleDotCom,
} from '../../util/validators';
import { isChangeEmailWrongPassword, isChangeEmailTakenError } from '../../util/errors';

import css from './ModalUpdateCredentials.module.css';

const MIN_PASSWORD_LENGTH = 8;

const ModalUpdateCredentialsComponent = props => {
  const {
    id,
    className,
    rootClassName,
    isOpen,
    onClose,
    onManageDisableScrolling,
    currentUser,
    onSubmit,
    inProgress,
    error,
    intl,
  } = props;

  const [passwordStrength, setPasswordStrength] = useState('');

  const classes = classNames(rootClassName || css.root, className);

  const currentEmail = currentUser?.attributes?.email || '';
  const isGeneratedEmail = currentEmail.includes('telia-') && currentEmail.includes('@paikallispalvelut.fi');

  // Password strength checker
  const checkPasswordStrength = (password) => {
    if (!password || password.length < MIN_PASSWORD_LENGTH) {
      return 'weak';
    }

    let strength = 0;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength >= 4) return 'strong';
    if (strength >= 3) return 'medium';
    return 'weak';
  };

  const passwordMinLength = (message) => (value) => {
    return value && value.length < MIN_PASSWORD_LENGTH ? message : undefined;
  };

  const passwordMinLengthMessage = intl.formatMessage({
    id: 'ModalUpdateCredentials.passwordTooShort',
  });

  const emailFormatMessage = intl.formatMessage({
    id: 'ModalUpdateCredentials.emailInvalid',
  });
  const emailDomainMessage = intl.formatMessage({
    id: 'ModalUpdateCredentials.emailInvalid',
  });

  const requiredMessage = intl.formatMessage({
    id: 'ModalUpdateCredentials.requiredField',
  });

  const handleSubmit = (values) => {
    const { email, newPassword } = values;
    return onSubmit({ email, newPassword });
  };

  const emailTakenErrorText = intl.formatMessage({
    id: 'ModalUpdateCredentials.emailTakenError',
  });

  const wrongPasswordErrorText = intl.formatMessage({
    id: 'ModalUpdateCredentials.wrongPasswordError',
  });

  const genericErrorText = intl.formatMessage({
    id: 'ModalUpdateCredentials.genericError',
  });

  const isEmailTakenError = isChangeEmailTakenError(error);
  const isWrongPasswordError = isChangeEmailWrongPassword(error);

  const errorMessage = isEmailTakenError
    ? emailTakenErrorText
    : isWrongPasswordError
    ? wrongPasswordErrorText
    : error
    ? genericErrorText
    : null;

  return (
    <Modal
      id={id}
      containerClassName={classes}
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      closeButtonMessage={intl.formatMessage({ id: 'ModalUpdateCredentials.closeModal' })}
    >
      <div className={css.modalContent}>
        <h2 className={css.heading}>
          <FormattedMessage id="ModalUpdateCredentials.heading" />
        </h2>

        <p className={css.description}>
          <FormattedMessage id="ModalUpdateCredentials.description" />
        </p>

        {isGeneratedEmail && (
          <div className={css.currentEmailInfo}>
            <strong>
              <FormattedMessage id="ModalUpdateCredentials.currentEmail" />:
            </strong>{' '}
            <code className={css.emailCode}>{currentEmail}</code>
            <p className={css.tempEmailNote}>
              <FormattedMessage id="ModalUpdateCredentials.tempEmailNote" />
            </p>
          </div>
        )}

        <FinalForm
          onSubmit={handleSubmit}
          render={({ handleSubmit, pristine, invalid, form, values }) => {
            const passwordValue = values?.newPassword || '';
            const strength = checkPasswordStrength(passwordValue);
            
            // Update strength state when password changes
            if (strength !== passwordStrength) {
              setPasswordStrength(strength);
            }

            const submitDisabled = pristine || invalid || inProgress;

            return (
              <form className={css.form} onSubmit={handleSubmit}>
                {errorMessage && (
                  <div className={css.error}>
                    <p className={css.errorText}>{errorMessage}</p>
                  </div>
                )}

                <div className={css.field}>
                  <FieldTextInput
                    id="email"
                    name="email"
                    type="email"
                    label={intl.formatMessage({ id: 'ModalUpdateCredentials.emailLabel' })}
                    placeholder={intl.formatMessage({
                      id: 'ModalUpdateCredentials.emailPlaceholder',
                    })}
                    validate={composeValidators(
                      required(requiredMessage),
                      emailFormatValid(emailFormatMessage),
                      emailDomainNotExampleDotCom(emailDomainMessage)
                    )}
                    autoComplete="email"
                  />
                </div>

                <div className={css.field}>
                  <FieldTextInput
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    label={intl.formatMessage({ id: 'ModalUpdateCredentials.passwordLabel' })}
                    placeholder={intl.formatMessage({
                      id: 'ModalUpdateCredentials.passwordPlaceholder',
                    })}
                    validate={composeValidators(
                      required(requiredMessage),
                      passwordMinLength(passwordMinLengthMessage)
                    )}
                    autoComplete="new-password"
                  />
                  {passwordValue && (
                    <div className={css.passwordStrength}>
                      <span className={css.strengthLabel}>
                        <FormattedMessage id="ModalUpdateCredentials.passwordStrength" />:
                      </span>
                      <span className={classNames(css.strengthValue, css[strength])}>
                        <FormattedMessage id={`ModalUpdateCredentials.strength.${strength}`} />
                      </span>
                      <div className={css.strengthBar}>
                        <div className={classNames(css.strengthBarFill, css[strength])} />
                      </div>
                    </div>
                  )}
                </div>

                <div className={css.passwordRequirements}>
                  <p className={css.requirementsTitle}>
                    <FormattedMessage id="ModalUpdateCredentials.passwordRequirements" />
                  </p>
                  <ul className={css.requirementsList}>
                    <li className={passwordValue.length >= MIN_PASSWORD_LENGTH ? css.met : ''}>
                      <FormattedMessage
                        id="ModalUpdateCredentials.requirement.minLength"
                        values={{ count: MIN_PASSWORD_LENGTH }}
                      />
                    </li>
                    <li className={/[A-Z]/.test(passwordValue) ? css.met : ''}>
                      <FormattedMessage id="ModalUpdateCredentials.requirement.uppercase" />
                    </li>
                    <li className={/[a-z]/.test(passwordValue) ? css.met : ''}>
                      <FormattedMessage id="ModalUpdateCredentials.requirement.lowercase" />
                    </li>
                    <li className={/[0-9]/.test(passwordValue) ? css.met : ''}>
                      <FormattedMessage id="ModalUpdateCredentials.requirement.number" />
                    </li>
                  </ul>
                </div>

                <div className={css.buttonGroup}>
                  <Button
                    type="submit"
                    inProgress={inProgress}
                    disabled={submitDisabled}
                    className={css.submitButton}
                  >
                    {inProgress ? (
                      <IconSpinner />
                    ) : (
                      <FormattedMessage id="ModalUpdateCredentials.submitButton" />
                    )}
                  </Button>
                  <button
                    type="button"
                    className={css.skipButton}
                    onClick={onClose}
                    disabled={inProgress}
                  >
                    <FormattedMessage id="ModalUpdateCredentials.skipButton" />
                  </button>
                </div>

                <div className={css.infoSection}>
                  <p className={css.securityNote}>
                    <FormattedMessage id="ModalUpdateCredentials.securityNote" />
                  </p>
                  <p className={css.futureLoginNote}>
                    <FormattedMessage id="ModalUpdateCredentials.futureLoginNote" />
                  </p>
                </div>
              </form>
            );
          }}
        />
      </div>
    </Modal>
  );
};

ModalUpdateCredentialsComponent.defaultProps = {
  className: null,
  rootClassName: null,
  error: null,
  inProgress: false,
};

const { bool, string, func } = PropTypes;

ModalUpdateCredentialsComponent.propTypes = {
  id: string.isRequired,
  className: string,
  rootClassName: string,
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  currentUser: PropTypes.object.isRequired,
  onSubmit: func.isRequired,
  inProgress: bool,
  error: PropTypes.object,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(ModalUpdateCredentialsComponent);
